import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { firestore } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";

/* ---------- Layout ---------- */

const Page = styled.main`
  min-height: 70vh;
  padding: 40px 18px;
  background: #f7fafc;
`;

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const Hero = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  box-shadow: 0 12px 34px rgba(0, 0, 0, 0.06);
  border-radius: 18px;
  padding: 22px;
  margin-bottom: 18px;

  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 14px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Title = styled.h1`
  margin: 0;
  font-size: clamp(26px, 3vw, 44px);
  color: #111827;
  font-weight: 900;
  letter-spacing: 0.3px;
`;

const Subtitle = styled.p`
  margin: 10px 0 0;
  color: #4b5563;
  line-height: 1.6;
  max-width: 780px;
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const BtnGhost = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;

  padding: 10px 14px;
  border-radius: 999px;
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #111827;
  font-weight: 900;
  text-decoration: none;

  &:hover {
    background: #f9fafb;
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid rgba(15, 118, 110, 0.35);
    outline-offset: 3px;
  }
`;

/* ---------- Content Card ---------- */

const Card = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 18px;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.06);
  overflow: hidden;
`;

const CardHeader = styled.div`
  padding: 14px 18px;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const Badge = styled.div`
  font-size: 12px;
  font-weight: 900;
  color: #c37a47;
  background: rgba(195, 122, 71, 0.12);
  border: 1px solid rgba(195, 122, 71, 0.22);
  padding: 6px 10px;
  border-radius: 999px;
  white-space: nowrap;
`;

const CardBody = styled.div`
  padding: 18px;
`;

/* ---------- Rules typography ---------- */

const RulesHtml = styled.div`
  color: #111827;

  /* Bas-typografi */
  font-size: 16px;
  line-height: 1.75;

  /* Snyggare rubriker om HTML har h2/h3 */
  h1, h2, h3 {
    color: #111827;
    font-weight: 900;
    line-height: 1.25;
    margin: 18px 0 10px;
  }

  h2 { font-size: 22px; }
  h3 { font-size: 18px; }

  p {
    margin: 0 0 12px;
    color: #374151;
  }

  ul, ol {
    margin: 10px 0 14px 20px;
    color: #374151;
  }

  li {
    margin: 6px 0;
  }

  a {
    color: #0f766e;
    font-weight: 800;
    text-decoration: underline;
  }

  blockquote {
    margin: 14px 0;
    padding: 12px 14px;
    border-left: 4px solid rgba(15, 118, 110, 0.45);
    background: rgba(15, 118, 110, 0.06);
    border-radius: 12px;
    color: #111827;
  }

  hr {
    border: none;
    border-top: 1px solid #e5e7eb;
    margin: 18px 0;
  }
`;

/* ---------- Loading / empty states ---------- */

const Skeleton = styled.div`
  height: 220px;
  border-radius: 14px;
  background: linear-gradient(90deg, #f3f4f6, #fafafa, #f3f4f6);
  background-size: 200% 100%;
  animation: shimmer 1.1s infinite linear;

  @keyframes shimmer {
    0% { background-position: 0% 0; }
    100% { background-position: 200% 0; }
  }
`;

const Notice = styled.div`
  padding: 14px 16px;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #374151;
  font-weight: 700;
`;

/* ---------- Minimal sanitization (better than raw) ---------- */
function sanitizeHtmlMinimally(input) {
  if (!input) return "";

  // 1) remove script tags
  let out = input.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "");

  // 2) remove inline event handlers like onclick=, onerror= etc.
  out = out.replace(/\son\w+="[^"]*"/gi, "");
  out = out.replace(/\son\w+='[^']*'/gi, "");

  // 3) block javascript: urls
  out = out.replace(/href\s*=\s*["']\s*javascript:[^"']*["']/gi, 'href="#"');

  return out;
}

const Rules = () => {
  const [rulesHtml, setRulesHtml] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRules = async () => {
      try {
        const docRef = doc(firestore, "rules", "general");
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const fetched = docSnap.data()?.rules || "";
          setRulesHtml(fetched);
        } else {
          setRulesHtml("");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchRules();
  }, []);

  const safeHtml = useMemo(() => sanitizeHtmlMinimally(rulesHtml), [rulesHtml]);

  return (
    <>
      <SEO
        title="Regler"
        description="Regler och information för besök och spel hos Hofors BGK."
        keywords="regler, bangolf, minigolf, hofors bgk"
      />

      <Page>
        <Container>
          <Hero>
            <div>
              <Title>Regler</Title>
              <Subtitle>
                Här hittar du regler för spel och trivsel. Om något är oklart – kontakta oss så hjälper vi dig.
              </Subtitle>
            </div>

            <Actions>
              <BtnGhost to="/">Tillbaka till startsidan</BtnGhost>
            </Actions>
          </Hero>

          <Card>
            <CardHeader>
              <div style={{ fontWeight: 900, color: "#111827" }}>
                Spelregler & information
              </div>
              <Badge>Uppdateras löpande</Badge>
            </CardHeader>

            <CardBody>
              {loading ? (
                <Skeleton />
              ) : safeHtml ? (
                <RulesHtml dangerouslySetInnerHTML={{ __html: safeHtml }} />
              ) : (
                <Notice>Inga regler tillgängliga just nu.</Notice>
              )}
            </CardBody>
          </Card>
        </Container>
      </Page>
    </>
  );
};

export default Rules;



